/**
 * Chaos Document Repository
 *
 * FERP Principles:
 * - Domain-agnostic: Handles documents from any domain (healthcare PDFs, financial forms, legal contracts)
 * - Data-driven: All document metadata from backend (DynamoDB), content in S3
 * - Repository pattern: All data access through this repository
 * - Life-critical: Complete audit trail for AI-generated template provenance
 * - Single code path: Same repository for all chaos ingestion workflows
 */

import { gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Types (from backend GraphQL schema)
// ============================================================================

export enum DocumentStatus {
    UPLOADED = 'UPLOADED',
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
    FAILED = 'FAILED',
    ARCHIVED = 'ARCHIVED',
}

export interface ChaosDocument {
    documentId: string;
    tenant: string;
    filename: string;
    mimeType: string;
    intakeTarget?: IntakeTarget;
    fileSize: number;
    status: DocumentStatus;
    uploadedAt: string;
    uploadedBy: string;
    latestConversion?: {
        conversionId: string;
        templateId: string;
        templateVersion: string;
        confidence: number;
        convertedAt: string;
    } | null;
}

export interface ChaosDocumentDetails extends ChaosDocument {
    s3Bucket: string;
    s3Key: string;
    extractedMetadata?: {
        documentType: string;
        detectedDomain: string;
        complexity: string;
        fieldCount: number;
        sectionCount: number;
        confidence: number;
        textPreview?: string | null;
        analyzedAt: string;
        engineVersion: string;
    } | null;
    conversionHistory: Array<{
        conversionId: string;
        convertedAt: string;
        convertedBy: string;
        status: string;
        confidence: number;
        primitivesCount: number;
        violationsCount: number;
        templateId?: string | null;
        templateVersion?: string | null;
        errorMessage?: string | null;
    }>;
}

// DRR-137: Intake target — what FERP should do with this ingested item
export type IntakeTarget =
    | 'DOCUMENT'
    | 'REGULATORY_DOCUMENT'
    | 'TASK_GROUP_DEFINITION'
    | 'FORM_TEMPLATE'
    | 'EVIDENCE_PACKAGE'
    | 'STRUCTURED_IMPORT'
    | 'CARE_VOCABULARY';

export interface UploadChaosDocumentInput {
    tenant: string;
    filename: string;
    mimeType: string;
    fileSize: number;
    uploadedBy: string;
    intakeTarget?: IntakeTarget;
    // NOTE: fileContent NOT included - actual file upload happens via separate S3 presigned URL flow
    // This mutation only creates the metadata record in DynamoDB after S3 upload completes
    // Backend schema: graphql_gateway/src/metadata_api/chaos_to_dsl_resolvers.rs (GqlUploadChaosDocumentInput)
    // TODO: Implement S3 presigned URL upload flow when backend supports it
}

export interface ConversionJob {
    jobId: string;
    tenant: string;
    documentId: string;
    status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
    templateId?: string;
    confidence?: number;
    createdAt: string;
    completedAt?: string;
    errorMessage?: string;
}

export interface ConvertChaosToDSLInput {
    tenant: string;
    documentId: string;
    targetDomain?: string;
    strictValidation?: boolean;
    includeEvidence?: boolean;
    createdBy: string;
}

export interface ConversionResult {
    template: {
        templateId: string;
        templateVersion: string;
        tenant: string;
        domain: string;
        templateType: string;
        metadata: any;
        createdBy: string;
        createdAt?: string;
        updatedAt?: string;
    };
    evidence: {
        fieldEvidence: any;
        primitiveEvidence: any;
        businessEvidence: any;
        extractionContext: any;
    };
    confidence: {
        overall: number;
        business: number;
        ui: number;
        primitives: number;
        mappings: number;
        compliance: number;
    };
    requiresHumanReview: boolean;
    generationMetadata: {
        model: string;
        generatedAt: string;
        tokensUsed?: number;
        processingTimeMs?: number;
    };
}

// ============================================================================
// GraphQL Queries & Mutations
// ============================================================================

const UPLOAD_CHAOS_DOCUMENT = gql`
    mutation UploadChaosDocument($input: GqlUploadChaosDocumentInput!) {
        uploadChaosDocument(input: $input) {
            documentId
            tenant
            filename
            mimeType
            fileSize
            status
            uploadedAt
            uploadedBy
            intakeTarget
        }
    }
`;

const LIST_CHAOS_DOCUMENTS = gql`
    query ListChaosDocuments($tenant: String!, $statusFilter: ChaosDocumentStatus, $limit: Int) {
        listChaosDocuments(tenant: $tenant, statusFilter: $statusFilter, limit: $limit) {
            documentId
            tenant
            filename
            mimeType
            fileSize
            status
            uploadedAt
            uploadedBy
            intakeTarget
        }
    }
`;

const GET_CHAOS_DOCUMENT_DETAILS = gql`
    query GetChaosDocumentDetails($tenant: String!, $documentId: String!) {
        getChaosDocumentDetails(tenant: $tenant, documentId: $documentId) {
            documentId
            tenant
            filename
            mimeType
            fileSize
            s3Bucket
            s3Key
            status
            uploadedAt
            uploadedBy
            extractedMetadata {
                documentType
                detectedDomain
                complexity
                fieldCount
                sectionCount
                confidence
                textPreview
                analyzedAt
                engineVersion
            }
            conversionHistory {
                conversionId
                convertedAt
                convertedBy
                status
                confidence
                primitivesCount
                violationsCount
                templateId
                templateVersion
                errorMessage
            }
            latestConversion {
                conversionId
                templateId
                templateVersion
                confidence
                convertedAt
            }
        }
    }
`;

const GET_CONVERSION_STATUS = gql`
    query GetConversionStatus($tenant: String!, $jobId: String!) {
        getConversionStatus(tenant: $tenant, jobId: $jobId) {
            jobId
            tenant
            documentId
            status
            templateId
            confidence
            createdAt
            completedAt
            errorMessage
        }
    }
`;

const DELETE_CHAOS_DOCUMENT = gql`
    mutation DeleteChaosDocument($tenant: String!, $documentId: String!) {
        deleteChaosDocument(tenant: $tenant, documentId: $documentId)
    }
`;

const CONVERT_CHAOS_TO_DSL = gql`
    mutation ConvertChaosToDSL($input: ConvertChaosToDSLInput!) {
        convertChaosToDSL(input: $input) {
            template {
                templateId
                templateVersion
                tenant
                domain
                templateType
                metadata
                createdBy
                createdAt
                updatedAt
            }
            evidence {
                fieldEvidence
                primitiveEvidence
                businessEvidence
                extractionContext
            }
            confidence {
                overall
                business
                ui
                primitives
                mappings
                compliance
            }
            requiresHumanReview
            generationMetadata {
                model
                generatedAt
                tokensUsed
                processingTimeMs
            }
        }
    }
`;

// DRR-137 Phase 4: Intake conversation types
export type MessageRole = 'USER' | 'ASSISTANT';

export interface IntakeConversationMessage {
    messageId: string;
    documentId: string;
    tenant: string;
    role: MessageRole;
    content: string;
    createdAt: string;
}

export interface SendIntakeMessageInput {
    documentId: string;
    tenant: string;
    content: string;
    sentBy: string;
}

const SEND_INTAKE_MESSAGE = gql`
    mutation SendIntakeMessage($input: SendIntakeMessageInput!) {
        sendIntakeMessage(input: $input) {
            messageId
            documentId
            tenant
            role
            content
            createdAt
        }
    }
`;

const GET_INTAKE_CONVERSATION = gql`
    query GetIntakeConversation($tenant: String!, $documentId: String!) {
        getIntakeConversation(tenant: $tenant, documentId: $documentId) {
            messageId
            documentId
            tenant
            role
            content
            createdAt
        }
    }
`;

// DRR-137 Phase 3: Evidence attachment types
export type EvidenceEntityType = 'TASK' | 'INSTANCE' | 'WORKFLOW' | 'ISSUE';

export interface AttachEvidenceInput {
    documentId: string;
    entityId: string;
    entityType: EvidenceEntityType;
    tenant: string;
    attachedBy: string;
    notes?: string;
}

export interface EvidenceAttachment {
    documentId: string;
    entityId: string;
    entityType: EvidenceEntityType;
    tenant: string;
    attachedAt: string;
    attachedBy: string;
    notes?: string | null;
}

const ATTACH_EVIDENCE = gql`
    mutation AttachEvidence($input: AttachEvidenceInput!) {
        attachEvidence(input: $input) {
            documentId
            entityId
            entityType
            tenant
            attachedAt
            attachedBy
            notes
        }
    }
`;

// ── Structured Import (DRR-137 Phase 5) ──────────────────────────────────────

export type ImportPrimitiveType = 'ACTOR';

export type ImportJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface ImportRowResult {
    rowNum: number;
    entityId?: string | null;
    /** "created" | "skipped" | "error" */
    status: string;
    error?: string | null;
    /** Name of the row for audit display (DRR-128: no PII) */
    inputSummary: string;
}

export interface StructuredImportJob {
    jobId: string;
    documentId: string;
    tenant: string;
    primitiveType: ImportPrimitiveType;
    status: ImportJobStatus;
    totalRows: number;
    processedRows: number;
    createdCount: number;
    errorCount: number;
    results: ImportRowResult[];
    createdAt: string;
    createdBy: string;
    completedAt?: string | null;
    errorMessage?: string | null;
}

export interface InitiateStructuredImportInput {
    documentId: string;
    tenant: string;
    initiatedBy: string;
    primitiveType: ImportPrimitiveType;
    /** Raw CSV content (max 100 rows). Frontend downloads from presigned URL and passes here. */
    csvContent: string;
}

const INITIATE_STRUCTURED_IMPORT = gql`
    mutation InitiateStructuredImport($input: InitiateStructuredImportInput!) {
        initiateStructuredImport(input: $input) {
            jobId
            documentId
            tenant
            primitiveType
            status
            totalRows
            processedRows
            createdCount
            errorCount
            results {
                rowNum
                entityId
                status
                error
                inputSummary
            }
            createdAt
            createdBy
            completedAt
            errorMessage
        }
    }
`;

const GET_STRUCTURED_IMPORT_JOB = gql`
    query GetStructuredImportJob($tenant: String!, $documentId: String!, $jobId: String!) {
        getStructuredImportJob(tenant: $tenant, documentId: $documentId, jobId: $jobId) {
            jobId
            documentId
            tenant
            primitiveType
            status
            totalRows
            processedRows
            createdCount
            errorCount
            results {
                rowNum
                entityId
                status
                error
                inputSummary
            }
            createdAt
            createdBy
            completedAt
            errorMessage
        }
    }
`;

const ACTOR_IMPORT_CSV_TEMPLATE = gql`
    query ActorImportCsvTemplate {
        actorImportCsvTemplate
    }
`;

// ============================================================================
// Repository Implementation
// ============================================================================

/**
 * Chaos Document Repository
 *
 * Provides domain-agnostic access to chaos documents and conversion jobs
 */
export class ChaosDocumentRepository extends BaseRepository<ChaosDocument> {
    /**
     * Upload chaos document
     *
     * Domain-agnostic: Handles PDFs, Word docs, scanned images, voice recordings
     *
     * @param input - Upload metadata (tenant, filename, mime type, size, uploader)
     * @returns Created chaos document record
     */
    async uploadDocument(input: UploadChaosDocumentInput): Promise<ChaosDocument> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: UPLOAD_CHAOS_DOCUMENT,
                variables: { input },
            });

            if (!result.data?.uploadChaosDocument) {
                throw new Error('Failed to upload chaos document');
            }

            // Invalidate documents list cache
            this.cache.invalidate('chaos_documents');

            return result.data?.uploadChaosDocument ?? null;
        } catch (error) {
            console.error('Failed to upload chaos document:', error);
            throw new Error(`Failed to upload chaos document: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * List chaos documents for tenant
     *
     * @param tenant - Tenant ID
     * @param statusFilter - Optional status filter (UPLOADED, PROCESSING, PROCESSED, FAILED)
     * @param limit - Maximum number of documents to return
     * @returns List of chaos documents
     */
    async listDocuments(
        tenant: string,
        statusFilter?: DocumentStatus,
        limit?: number
    ): Promise<ChaosDocument[]> {
        try {
            const cacheKey = `chaos_documents_${tenant}_${statusFilter || 'all'}_${limit || 'all'}`;
            const cached = this.cache.get(cacheKey, 60000); // Cache for 1 minute

            if (cached) {
                return cached;
            }

            const result = await this.graphQLClient.query<Record<string, any>>({
                query: LIST_CHAOS_DOCUMENTS,
                variables: { tenant, statusFilter, limit },
            });

            const documents = result.data?.listChaosDocuments || [];
            this.cache.set(cacheKey, documents);

            return documents;
        } catch (error) {
            console.error('Failed to list chaos documents:', error);
            throw new Error(`Failed to list chaos documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get chaos document details
     *
     * @param tenant - Tenant ID
     * @param documentId - Document ID
     * @returns Detailed chaos document information
     */
    async getDocumentDetails(tenant: string, documentId: string): Promise<ChaosDocumentDetails | null> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: GET_CHAOS_DOCUMENT_DETAILS,
                variables: { tenant, documentId },
            });

            return result.data?.getChaosDocumentDetails || null;
        } catch (error) {
            console.error('Failed to get chaos document details:', error);
            throw new Error(`Failed to get chaos document details: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get conversion job status
     *
     * @param tenant - Tenant ID
     * @param jobId - Conversion job ID
     * @returns Conversion job status
     */
    async getConversionStatus(tenant: string, jobId: string): Promise<ConversionJob | null> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: GET_CONVERSION_STATUS,
                variables: { tenant, jobId },
            });

            return result.data?.getConversionStatus || null;
        } catch (error) {
            console.error('Failed to get conversion status:', error);
            throw new Error(`Failed to get conversion status: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Delete chaos document
     *
     * Life-critical: Soft delete only (preserves audit trail)
     *
     * @param tenant - Tenant ID
     * @param documentId - Document ID
     * @returns True if deleted successfully
     */
    async deleteDocument(tenant: string, documentId: string): Promise<boolean> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: DELETE_CHAOS_DOCUMENT,
                variables: { tenant, documentId },
            });

            // Invalidate cache
            this.cache.invalidate('chaos_documents');
            this.cache.invalidate(documentId);

            return result.data?.deleteChaosDocument || false;
        } catch (error) {
            console.error('Failed to delete chaos document:', error);
            throw new Error(`Failed to delete chaos document: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Convert chaos document to Metadata DSL template
     *
     * FERP Principles:
     * - Repository pattern: All conversion through backend GraphQL mutation
     * - Data-driven: Conversion logic from ChaosToMetadataDSLConverter service
     * - Life-critical: Complete audit trail for AI-generated templates
     * - Domain-agnostic: Works for all domains via metadata
     *
     * @param input - Conversion parameters
     * @returns Conversion result with template, evidence, and confidence metrics
     */
    async convertToDSL(input: ConvertChaosToDSLInput): Promise<ConversionResult> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: CONVERT_CHAOS_TO_DSL,
                variables: { input },
            });

            if (!result.data?.convertChaosToDSL) {
                throw new Error('Failed to convert chaos to DSL');
            }

            // Invalidate caches (document status will change to CONVERTED)
            this.cache.invalidate('chaos_documents');
            this.cache.invalidate(input.documentId);

            return result.data?.convertChaosToDSL ?? null;
        } catch (error) {
            console.error('Failed to convert chaos to DSL:', error);
            throw new Error(`Failed to convert chaos to DSL: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Trigger chaos-to-DSL conversion
     *
     * @param tenant - Tenant ID
     * @param documentId - Document ID to convert
     * @returns Created conversion job
     */
    async triggerConversion(tenant: string, documentId: string): Promise<ConversionJob> {
        try {
            // TODO: Implement when backend mutation is added
            // const result = await this.graphQLClient.mutate<Record<string, any>>({
            //     mutation: CONVERT_CHAOS_TO_DSL,
            //     variables: { tenant, documentId },
            // });

            throw new Error('triggerConversion not yet implemented in backend');
        } catch (error) {
            console.error('Failed to trigger chaos conversion:', error);
            throw new Error(`Failed to trigger chaos conversion: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Attach a chaos document to an existing FERP entity via a typed EVIDENCED_BY edge (DRR-137 Phase 3, DRR-110).
     *
     * @param input - Evidence attachment details
     * @returns Created evidence attachment edge
     */
    async attachEvidence(input: AttachEvidenceInput): Promise<EvidenceAttachment> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: ATTACH_EVIDENCE,
                variables: { input },
            });

            if (!result.data?.attachEvidence) {
                throw new Error('attachEvidence returned no data');
            }

            return result.data.attachEvidence;
        } catch (error) {
            console.error('Failed to attach evidence:', error);
            throw new Error(`Failed to attach evidence: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Send a user message in an intake conversation and receive the AI response (DRR-137 Phase 4).
     *
     * @param input - Message content and session context
     * @returns The AI assistant's response message
     */
    async sendIntakeMessage(input: SendIntakeMessageInput): Promise<IntakeConversationMessage> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: SEND_INTAKE_MESSAGE,
                variables: { input },
            });

            if (!result.data?.sendIntakeMessage) {
                throw new Error('sendIntakeMessage returned no data');
            }

            return result.data.sendIntakeMessage;
        } catch (error) {
            console.error('Failed to send intake message:', error);
            throw new Error(`Failed to send intake message: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Fetch the full conversation history for an intake session (DRR-137 Phase 4).
     *
     * @param tenant - Tenant ID
     * @param documentId - The intake document ID
     * @returns Ordered list of conversation messages (chronological)
     */
    async getIntakeConversation(tenant: string, documentId: string): Promise<IntakeConversationMessage[]> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: GET_INTAKE_CONVERSATION,
                variables: { tenant, documentId },
                fetchPolicy: 'network-only',
            });

            return result.data?.getIntakeConversation ?? [];
        } catch (error) {
            console.error('Failed to fetch intake conversation:', error);
            throw new Error(`Failed to fetch conversation: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    // ── Structured Import (DRR-137 Phase 5) ────────────────────────────────

    /**
     * Initiate a structured CSV import job (DRR-137 Phase 5).
     *
     * Parses the CSV, creates Actor primitives via repository+registry, and returns
     * the completed StructuredImportJob with per-row results.
     *
     * @param input - CSV content + metadata (document_id, tenant, initiated_by, primitive_type)
     * @returns Completed import job with per-row results
     */
    async initiateStructuredImport(input: InitiateStructuredImportInput): Promise<StructuredImportJob> {
        try {
            const result = await this.graphQLClient.mutate<Record<string, any>>({
                mutation: INITIATE_STRUCTURED_IMPORT,
                variables: { input },
            });

            if (!result.data?.initiateStructuredImport) {
                throw new Error('initiateStructuredImport returned no data');
            }

            return result.data.initiateStructuredImport;
        } catch (error) {
            console.error('Failed to initiate structured import:', error);
            throw new Error(`Failed to initiate import: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Fetch a previously-run structured import job (DRR-137 Phase 5).
     *
     * @param tenant - Tenant ID
     * @param documentId - Source ChaosDocument ID
     * @param jobId - Import job ID (returned by initiateStructuredImport)
     * @returns The import job with full row results
     */
    async getStructuredImportJob(tenant: string, documentId: string, jobId: string): Promise<StructuredImportJob> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: GET_STRUCTURED_IMPORT_JOB,
                variables: { tenant, documentId, jobId },
                fetchPolicy: 'network-only',
            });

            if (!result.data?.getStructuredImportJob) {
                throw new Error('getStructuredImportJob returned no data');
            }

            return result.data.getStructuredImportJob;
        } catch (error) {
            console.error('Failed to fetch import job:', error);
            throw new Error(`Failed to fetch import job: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Download an Actor import CSV template with example rows (DRR-137 Phase 5).
     *
     * @returns CSV string with headers + example actor rows
     */
    async getActorImportCsvTemplate(): Promise<string> {
        try {
            const result = await this.graphQLClient.query<Record<string, any>>({
                query: ACTOR_IMPORT_CSV_TEMPLATE,
                fetchPolicy: 'cache-first',
            });

            return result.data?.actorImportCsvTemplate ?? '';
        } catch (error) {
            console.error('Failed to fetch CSV template:', error);
            throw new Error(`Failed to fetch template: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
}

// Export singleton instance
export const chaosDocumentRepository = new ChaosDocumentRepository();
